import { useEffect, useState } from 'react';

/**
 * 判断当前是否处于微信内置浏览器（含移动端微信、PC 端微信，以及企业微信）。
 *
 * 判据：微信内置 WebView 的 UserAgent 一律包含 "MicroMessenger"
 *   - 移动端微信：含 MicroMessenger
 *   - PC 端微信：含 MicroMessenger，并另带 WindowsWechat
 *   - 企业微信：含 MicroMessenger，并另带 wxwork
 * 三者的内置浏览器都对文件下载有限制，故统一拦截引导。这是社区公认的稳定判据。
 * 后端 redemption.controller 用同一判据在「券码管理」标记微信内兑换。
 */
function isWechatBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /micromessenger/i.test(navigator.userAgent);
}

// 本站规范域名：拦截层引导用户复制到系统浏览器打开（带当前路径，便于回到原页面）。
const CANONICAL_ORIGIN = 'https://www.zxdata.cc';

function canonicalUrl(): string {
  if (typeof window === 'undefined') return `${CANONICAL_ORIGIN}/`;
  const { pathname, search, hash } = window.location;
  return `${CANONICAL_ORIGIN}${pathname}${search}${hash}`;
}

// 走完 3 道警告后的「本会话已确认」标记：本会话不再重复拦（换会话/重开仍会再次拦）。
const PASSED_KEY = 'zxdata.wechatGuardPassed.v2';

const TOTAL_STEPS = 3;
const READ_SECONDS = 5;

// 三道警告，措辞逐级加重；第 3 道为免责声明（券码丢失/下载失败不退款）。
const STEPS: { heading: string; body: JSX.Element; cta: string }[] = [
  {
    heading: '请勿在微信内打开本站',
    body: (
      <>
        您正在<span className="font-semibold text-white">微信内置浏览器</span>中打开本站。微信会拦截文件下载，
        数据包<span className="font-semibold text-white">很可能下载失败且不会报错</span>。请复制网址，改用电脑浏览器，
        或登录后用手机系统浏览器（Safari / Chrome / Edge）打开。
      </>
    ),
    cta: '我已知晓，继续',
  },
  {
    heading: '再次提醒：微信内操作有风险',
    body: (
      <>
        在微信内<span className="font-semibold text-white">兑换券码、下载数据包都可能失败</span>。一旦券码在微信内被消耗、
        而文件未能下载成功，将<span className="font-semibold text-white">很难恢复</span>。强烈建议立即复制网址，
        换用系统浏览器打开后再操作。
      </>
    ),
    cta: '我已知晓，继续',
  },
  {
    heading: '最终确认 · 免责声明',
    body: (
      <>
        若您仍坚持在微信内继续，
        <span className="font-semibold text-[#f3cd6e]">
          本站不承担任何券码丢失、未下载成功的责任，且无法退款
        </span>
        。点击下方按钮继续，即视为您已阅读并接受上述全部风险。
      </>
    ),
    cta: '我已知晓风险，仍在微信内继续',
  },
];

/**
 * 微信内置浏览器拦截引导（2026-06-24 强化为「3 道警告 + 每道 5 秒强制阅读」的硬门）。
 *
 * 本站核心功能是下载数据包 / 兑换券码，而微信内置浏览器会拦截、阻断文件下载。因此一旦检测到
 * 处于微信环境，立即铺满全屏的深色拦截层，用户必须依次读完 3 道警告（每道 5 秒倒计时内「继续」
 * 按钮禁用），且每道都提供「复制网址」，第 3 道为免责声明，确认后才放行进入站点本体。
 * 去掉了旧版的单步软出口——现在唯一进入方式是读完并确认全部 3 道。
 *
 * 该层为「刻意深色」的全屏 scrim，按规范使用字面深色（不走语义 token、不随主题翻转）。
 */
export function WechatBrowserGuard() {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0); // 0-based
  const [remaining, setRemaining] = useState(READ_SECONDS);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isWechatBrowser()) return;
    let passed = false;
    try {
      passed = sessionStorage.getItem(PASSED_KEY) === '1';
    } catch {
      /* 隐私模式 sessionStorage 不可用：当作未确认过，正常拦截 */
    }
    if (passed) return;
    setShow(true);
    document.documentElement.classList.add('overflow-hidden');
    return () => document.documentElement.classList.remove('overflow-hidden');
  }, []);

  // 每进入一道警告，重置 5 秒倒计时（倒计时内「继续」禁用）。
  useEffect(() => {
    if (!show) return;
    setRemaining(READ_SECONDS);
    const id = window.setInterval(() => {
      setRemaining((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [show, step]);

  if (!show) return null;

  async function copyUrl() {
    const url = canonicalUrl();
    let ok = false;
    try {
      // 优先用异步剪贴板 API
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      // 微信 WebView 常不支持 clipboard API：退回 execCommand 兜底
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }

  function proceed() {
    if (remaining > 0) return; // 倒计时未走完，不可继续
    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
      setCopied(false);
    } else {
      // 走完全部 3 道：放行并标记本会话已确认
      setShow(false);
      document.documentElement.classList.remove('overflow-hidden');
      try {
        sessionStorage.setItem(PASSED_KEY, '1');
      } catch {
        /* 忽略：隐私模式下次进入会再次拦截 */
      }
    }
  }

  const current = STEPS[step];
  const canProceed = remaining === 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/90 text-white backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="请在浏览器中打开本站"
    >
      {/* 右上角引导：弯箭头指向「···」菜单 */}
      <div className="pointer-events-none absolute right-4 top-2 flex w-[230px] flex-col items-end">
        <svg
          viewBox="0 0 160 120"
          className="h-[88px] w-[120px] text-[#f3cd6e]"
          fill="none"
          stroke="currentColor"
          strokeWidth={3.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 110 C 78 110, 132 96, 140 24" />
          <path d="M124 30 L 140 22 L 146 40" />
        </svg>
        <p className="mt-1 text-right text-[13px] font-medium leading-snug text-[#f3cd6e]">
          点击右上角「···」
          <br />
          选择「在浏览器打开」
        </p>
      </div>

      {/* 中部说明卡 */}
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-2xl border border-white/15 bg-white/[0.06] p-7 text-center backdrop-blur-md">
          {/* 进度：第 N / 3 次提醒 + 圆点 */}
          <div className="mb-4 flex items-center justify-center gap-2">
            <span className="text-xs font-medium tracking-wide text-white/70">
              第 {step + 1} / {TOTAL_STEPS} 次提醒
            </span>
            <span className="flex gap-1.5">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 w-1.5 rounded-full ${
                    i <= step ? 'bg-[#f3cd6e]' : 'bg-white/25'
                  }`}
                />
              ))}
            </span>
          </div>

          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f3cd6e]/15">
            <svg
              viewBox="0 0 24 24"
              className="h-7 w-7 text-[#f3cd6e]"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
          </div>

          <h2 className="mt-5 text-lg font-semibold">{current.heading}</h2>
          <p className="mt-3 text-sm leading-relaxed text-white/80">{current.body}</p>

          {/* 规范域名，便于手动读/输入 */}
          <p className="mt-4 break-all rounded-lg bg-black/40 px-3 py-2 text-xs text-[#f3cd6e]">
            {canonicalUrl()}
          </p>

          <div className="mt-5 space-y-3">
            <button
              type="button"
              onClick={copyUrl}
              className="w-full rounded-xl border border-[#f3cd6e]/60 px-4 py-3 text-sm font-semibold text-[#f3cd6e] transition-opacity hover:opacity-90 active:opacity-80"
            >
              {copied ? '已复制网址' : '复制网址'}
            </button>
            <button
              type="button"
              onClick={proceed}
              disabled={!canProceed}
              className={`w-full rounded-xl px-4 py-3 text-sm font-semibold transition-opacity ${
                canProceed
                  ? 'bg-[#f3cd6e] text-[#1a1206] hover:opacity-90 active:opacity-80'
                  : 'cursor-not-allowed bg-white/10 text-white/45'
              }`}
            >
              {canProceed ? current.cta : `请仔细阅读（${remaining}s）`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
